import React, { useEffect, useState } from "react";
import Footer from "../../core/common/footer/Footer";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router";
import { all_routes } from "../router/all_routes";
import CandidateDetails from "./components/CandidateDetails";
import StepProgress from "./components/StepProgress";
import axios from "axios";
import { apibaseurl } from "../../environment";
import logo from "../../images/logo.png";

import SnipperLoader from "../../core/common/loader/SnipperLoader";
const LocationVerification = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<
    "checking" | "valid" | "invalid"
  >("checking");
  const [tokenError, setTokenError] = useState("");
  const [addressLoading, setAddressLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setCandidateId(null);
    setTokenStatus("checking");
    setAddressLoading(true);
    setDetailData(null);
    setLatitude("");
    setLongitude("");
    setAddressLatitude("");
    setAddressLongitude("");
    setLocationDistance(null);
    setMapUrl("");
    setLocationAllowed(false);
    setTokenError("");
    if (!token) {
      setTokenStatus("invalid");
      setTokenError("Verification token is missing.");
      setAddressLoading(false);
      return () => {
        active = false;
      };
    }
    const load = async () => {
      try {
        const verified = await axios.get(
          `${apibaseurl}/api/v1/candidates/addresses/validate-address-token/${encodeURIComponent(token)}`,
        );
        if (!active) return;
        const validation = verified.data?.data;
        if (validation?.valid !== true || !validation?.candidateId) {
          throw new Error(
            validation?.message ||
              verified.data?.message ||
              "Invalid or expired verification link.",
          );
        }
        const id = String(validation.candidateId);
        setCandidateId(id);
        const response = await axios.get(
          `${apibaseurl}/api/v1/candidates/addresses/${encodeURIComponent(id)}`,
          { headers: { Authorization: `Address ${token}` } },
        );
        if (!active) return;
        const data = Array.isArray(response.data?.data)
          ? response.data.data[0]
          : response.data?.data;
        if (!response.data?.success || !data)
          throw new Error("Unable to load candidate address.");
        setDetailData(data);
        if (
          data.address_latitude != null &&
          data.address_longitude != null &&
          data.address_latitude !== "" &&
          data.address_longitude !== ""
        ) {
          const lat = String(data.address_latitude);
          const lng = String(data.address_longitude);
          setLatitude(lat);
          setLongitude(lng);
          setOriginalLatitude(lat);
          setOriginalLongitude(lng);
          setLocationAllowed(true);
          setMapUrl(
            `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`,
          );
        }
        setTokenStatus("valid");
      } catch (error: any) {
        if (!active) return;
        setTokenStatus("invalid");
        setTokenError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to validate verification link.",
        );
      } finally {
        if (active) setAddressLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [token]);

  // ========= live location state ===
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  // ========= User enter address location state ===
  const [addressLatitude, setAddressLatitude] = useState("");
  const [addressLongitude, setAddressLongitude] = useState("");
  // ========= distance calculate  state ===
  const [locationDistance, setLocationDistance] = useState<number | null>(null);
  const [locationMismatch, setLocationMismatch] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [mapUrl, setMapUrl] = useState("");
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [locationError, setLocationError] = useState("");
  // const [signature, setSignature] = useState<File | null>(null);
  const [confirmAddress, setConfirmAddress] = useState(true);
  // ---------- Get Api Data State ------------------------
  const [detailData, setDetailData] = useState<any>(null);
  const [originalLatitude, setOriginalLatitude] = useState("");
  const [originalLongitude, setOriginalLongitude] = useState("");
  const getFullAddress = () => {
    if (!detailData) return "";
    return [
      detailData?.address_line1,
      detailData?.city,
      detailData?.state,
      detailData?.pincode,
      detailData?.country,
    ]
      .filter(Boolean)
      .join(", ");
  };
  const GOOGLE_MAPS_API_KEY = "AIzaSyApVnqHJ_X5QDl1nRj9Iqh784n-PPpdOfg";

  const getLatLngFromAddress = async (address: string) => {
    try {
      if (!address) return null;
      if (!GOOGLE_MAPS_API_KEY)
        throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not configured.");
      const win = window as any;
      if (!win.google?.maps?.Geocoder) {
        await new Promise<void>((resolve, reject) => {
          const existing = document.querySelector<HTMLScriptElement>(
            'script[data-address-geocoder="true"]',
          );
          if (existing) {
            if (win.google?.maps?.Geocoder) return resolve();
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener(
              "error",
              () => reject(new Error("Google Maps script failed")),
              { once: true },
            );
            return;
          }
          const script = document.createElement("script");
          script.dataset.addressGeocoder = "true";
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Google Maps script failed"));
          document.head.appendChild(script);
        });
      }
      const geocoder = new win.google.maps.Geocoder();
      const result = await geocoder.geocode({ address });
      const first = result.results?.[0];
      if (!first?.geometry?.location)
        throw new Error("No Google coordinates returned");
      return {
        latitude: first.geometry.location.lat(),
        longitude: first.geometry.location.lng(),
        formattedAddress: first.formatted_address,
      };
    } catch (error) {
      console.error("Geocoding Error:", error);
      return null;
    }
  };

  const handleGetLocation = async () => {
    const address = getFullAddress();
    if (!address) {
      console.log("Address not available");
      return;
    }
    const location = await getLatLngFromAddress(address);
    if (location) {
      // IMPORTANT
      setAddressLatitude(location.latitude.toString());
      setAddressLongitude(location.longitude.toString());
    }
  };
  useEffect(() => {
    if (!detailData) return;
    handleGetLocation();
  }, [detailData]);

  const calculateDistanceInMeters = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ) => {
    const R = 6371000; // Earth radius in metres
    const toRadians = (degree: number) => {
      return (degree * Math.PI) / 180;
    };
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };
  const compareLocations = (
    currentLat: number,
    currentLng: number,
    addressLat: number,
    addressLng: number,
  ) => {
    const distance = calculateDistanceInMeters(
      currentLat,
      currentLng,
      addressLat,
      addressLng,
    );
    setLocationDistance(distance);
    if (distance > 1000) {
      setLocationMismatch(true);
    } else {
      setLocationMismatch(false);
    }
  };
  useEffect(() => {
    if (
      latitude === "" ||
      longitude === "" ||
      addressLatitude === "" ||
      addressLongitude === ""
    ) {
      return;
    }
    const currentLat = Number(latitude);
    const currentLng = Number(longitude);
    const addressLat = Number(addressLatitude);
    const addressLng = Number(addressLongitude);
    if (
      !Number.isFinite(currentLat) ||
      !Number.isFinite(currentLng) ||
      !Number.isFinite(addressLat) ||
      !Number.isFinite(addressLng)
    ) {
      console.log("Invalid coordinates");
      return;
    }
    compareLocations(currentLat, currentLng, addressLat, addressLng);
  }, [latitude, longitude, addressLatitude, addressLongitude]);
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        icon: "error",
        title: "Location Not Supported",
        text: "Your browser doesn't support GPS.",
      });
      return;
    }
    setLocationLoading(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Save LIVE location
        setLatitude(lat.toString());
        setLongitude(lng.toString());
        setLocationAllowed(true);
        setMapUrl(
          `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`,
        );
        setLocationLoading(false);
        // DO NOT compare here.
        // useEffect will compare automatically.
      },
      (error) => {
        console.error("GPS Error:", error);
        setLocationLoading(false);
        setLocationAllowed(false);
        setLocationError(
          "Location access was denied or unavailable. Please enable GPS/location permissions and try again.",
        );
        Swal.fire({
          icon: "warning",
          title: "Location Permission Required",
          text: "Please enable your GPS location to continue.",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const goToNextStep = () => {
    const route = all_routes.candidateSelfieVerification;
    navigate(
      token ? route.replace(":token", encodeURIComponent(token)) : route,
    );
  };

  const handleNext = async () => {
    if (tokenStatus !== "valid" || !candidateId || !token || !detailData)
      return;

    if (!latitude || !longitude) {
      Swal.fire({
        icon: "warning",
        title: "Location Required",
        text: "Please share your current location before continuing.",
      });
      return;
    }

    if (!confirmAddress) {
      Swal.fire({
        icon: "warning",
        title: "Confirmation Required",
        text: "Please confirm that the above address is true and correct.",
      });
      return;
    }

    try {
      const currentLat = Number(latitude);
      const currentLng = Number(longitude);
      const addressLat = Number(addressLatitude);
      const addressLng = Number(addressLongitude);

      const isValidCoordinate = (
        value: string | number,
        min: number,
        max: number,
      ) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value)) &&
        Number(value) >= min &&
        Number(value) <= max;

      const hasValidCoordinates =
        isValidCoordinate(latitude, -90, 90) &&
        isValidCoordinate(longitude, -180, 180) &&
        isValidCoordinate(addressLatitude, -90, 90) &&
        isValidCoordinate(addressLongitude, -180, 180);

      // Calculate distance every time Save & Next is clicked.
      // No restriction for inside or outside 1 km.
      let calculatedDistance: number | null = null;

      if (hasValidCoordinates) {
        calculatedDistance = calculateDistanceInMeters(
          currentLat,
          currentLng,
          addressLat,
          addressLng,
        );

        setLocationDistance(calculatedDistance);
        setLocationMismatch(calculatedDistance > 1000);
      }

      const response = await axios.patch(
        `${apibaseurl}/api/v1/candidates/addresses/${encodeURIComponent(candidateId!)}/geo-location`,
        {
          address_type: detailData?.address_type,
          latitude,
          longitude,
          distance:
            calculatedDistance !== null ? String(calculatedDistance) : "",
        },
        {
          headers: {
            Authorization: `Address ${token}`,
          },
        },
      );

      if (response.data?.success) {
        goToNextStep();
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: response.data?.message || "Something went wrong.",
      });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  return (
    <>
      <header className="w-100 bg-white border-bottom">
        <div className="container-fluid px-3 px-md-4 px-lg-5">
          <div className="d-flex align-items-center justify-content-start">
            <img
              src={logo}
              alt="Company Logo"
              className="img-fluid"
              style={{ maxWidth: "140px", maxHeight: "55px" }}
            />
          </div>
        </div>
      </header>
      <div className="container">
        {addressLoading || tokenStatus === "checking" ? (
          <SnipperLoader />
        ) : tokenStatus === "invalid" ? (
          <div className="card border-danger p-4 text-center my-4">
            <h4 className="text-danger">
              Verification Link Invalid or Expired
            </h4>
            <p>{tokenError || "Please request a new verification link."}</p>
          </div>
        ) : (
          <>
            {/* Shared step tracker — this page is step 2 of 5, step 1 already done */}
            <StepProgress totalSteps={5} currentStep={2} completedSteps={[1]} />
            <CandidateDetails addressInfo={detailData} />
            <div className="card p-4 overflow-hidden">
              {/* Document header strip */}
              <div className="border-bottom pb-3 mb-4">
                <h5 className="fw-semibold">Address Authentication</h5>
                <p className="text-dark">
                  Verify your current location and sign below to authenticate
                  your address.
                </p>
              </div>
              <div>
                {/* Location block */}
                <div
                  className="d-flex align-items-start gap-2 p-3 mb-3 rounded-3"
                  style={{
                    background: "#FFF8F1",
                    border: "1px solid #F6D5B3",
                    borderLeft: "4px solid #E67E22",
                  }}
                >
                  <i
                    className="ti ti-info-circle mt-1 flex-shrink-0"
                    style={{
                      color: "#D97706",
                      fontSize: "18px",
                    }}
                  ></i>
                  <p
                    className="mb-0 text-dark"
                    style={{ fontSize: "14px", lineHeight: "1.5" }}
                  >
                    Please make sure you're available at the given address. If
                    the distance between the submitted address and your current
                    location is more than{" "}
                    <strong style={{ color: "#D97706" }}>1 kilometers</strong>,
                    it will create a discrepancy at the time of verification.
                  </p>
                </div>
                <div
                  className="card border-0 shadow-sm mb-4"
                  style={{
                    borderRadius: "16px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="card-body p-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #fff8f3 0%, #ffffff 55%, #fff4eb 100%)",
                    }}
                  >
                    <div className="row align-items-center g-2">
                      {/* Left Section */}
                      <div className="col-12 col-lg-8">
                        <div className="d-flex align-items-start">
                          {/* Location Icon */}
                          <div
                            className="me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: 52,
                              height: 52,
                              borderRadius: "50%",
                              background: "#FFF1E8",
                              color: "#F26522",
                            }}
                          >
                            <i className="ti ti-map-pin fs-22"></i>
                          </div>

                          {/* Content */}
                          <div className="flex-grow-1">
                            <h4 className="fw-bold mb-2">
                              Detect your location
                            </h4>

                            <p className="text-dark mb-3">
                              Your live location helps us verify your current
                              address during the background verification
                              process. Your location is only accessed after your
                              permission.
                            </p>

                            {/* Badges */}
                            <div className="d-flex flex-wrap gap-2">
                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: "#FFF1E8",
                                  color: "#F26522",
                                  padding: "8px 14px",
                                }}
                              >
                                <i className="ti ti-shield-check me-1"></i>
                                Secure
                              </span>

                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: "#E8F8EE",
                                  color: "#198754",
                                  padding: "8px 14px",
                                }}
                              >
                                <i className="ti ti-lock me-1"></i>
                                Privacy Protected
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Section */}
                      <div className="col-12 col-lg-4">
                        <div className="d-flex flex-column align-items-stretch align-items-lg-end">
                          {/* Status */}
                          <div className="mb-2 text-start text-lg-end">
                            {locationAllowed ? (
                              <small className="text-success fw-semibold">
                                <i className="ti ti-check me-1"></i>
                                Location Verified
                              </small>
                            ) : (
                              <small className="text-dark fw-semibold">
                                Click here to share your location
                              </small>
                            )}
                          </div>

                          {/* Button */}
                          <button
                            onClick={getCurrentLocation}
                            disabled={locationLoading}
                            className="btn px-4 py-2 w-lg-auto"
                            style={{
                              background: locationAllowed
                                ? "#198754"
                                : "#F26522",
                              color: "#fff",
                              border: "none",
                              borderRadius: "8px",
                              minHeight: "44px",
                            }}
                          >
                            {locationLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />
                                Detecting...
                              </>
                            ) : locationAllowed ? (
                              <>
                                <i className="ti ti-refresh me-2"></i>
                                {detailData?.address_latitude
                                  ? "Re-detect Location"
                                  : "Location Captured"}
                              </>
                            ) : (
                              <>
                                <i className="ti ti-current-location me-2"></i>
                                Share Location
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {locationError && (
                  <div
                    style={{
                      backgroundColor: "#fdeaea",
                      border: "1px solid #f6cdd0",
                      color: "#c0392b",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      fontSize: "13px",
                      marginBottom: "16px",
                    }}
                  >
                    {locationError}
                  </div>
                )}
                {/* ---- map start from here -------- */}
                <div className="card mt-4">
                  {/* Coordinates */}
                  {latitude && longitude && (
                    <>
                      <div className="row g-3 px-3 py-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-map-pin me-2 text-danger"></i>
                            Latitude
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={latitude}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-map-pin me-2 text-primary"></i>
                            Longitude
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={longitude}
                            readOnly
                          />
                        </div>
                      </div>
                      <hr style={{ borderColor: "#828386" }} />
                    </>
                  )}
                  {/* Google Map */}
                  <div
                    style={{
                      height: "400px",
                      overflow: "hidden",
                      borderRadius: "12px",
                    }}
                  >
                    {mapUrl ? (
                      <iframe
                        title="Google Map"
                        src={mapUrl}
                        width="100%"
                        height="100%"
                        style={{
                          border: 0,
                          display: "block",
                        }}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="d-flex flex-column justify-content-center align-items-center h-100"
                        style={{
                          background: "#fafafa",
                          color: "#adb5bd",
                        }}
                      >
                        <i
                          className="ti ti-map-2"
                          style={{ fontSize: "60px" }}
                        ></i>
                        <h5 className="mt-3">Map Preview</h5>
                        <p className="mb-0 text-muted">
                          Your location map will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* ---- map end from here -------- */}
                <hr style={{ margin: "32px 0", borderColor: "#919297" }} />
                {/* Confirmation block */}
                <h6 className="fw-semibold mb-2" style={{ color: "#2b1a0f" }}>
                  <i
                    className="ti ti-shield-check me-2"
                    style={{ color: "#F26522" }}
                  ></i>
                  Confirm your Address
                </h6>
                <div
                  className="d-flex gap-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setConfirmAddress(!confirmAddress)}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    style={{ flexShrink: 0 }}
                    checked={confirmAddress}
                    onChange={(e) => setConfirmAddress(e.target.checked)}
                  />
                  <p className="text-dark mb-0">
                    I confirm that the above address is true and correct to the
                    best of my knowledge. I authorize the use of my location and
                    submitted details for background verification purposes.
                  </p>
                </div>
                {/* <hr style={{ margin: "32px 0", borderColor: "#808186" }} /> */}
                {/* <div className="d-flex align-items-start gap-3 mb-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  backgroundColor: "#FFF4EB",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ti ti-signature"
                  style={{
                    color: "#F26522",
                    fontSize: "22px",
                  }}
                ></i>
              </div>
              <div>
                <h6 className="fw-semibold mb-1" style={{ color: "#2b1a0f" }}>
                  Digital Signature
                </h6>
                <p
                  className="text-muted mb-0"
                  style={{
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  Draw your signature below to acknowledge and authorize this
                  submission.
                </p>
              </div>
            </div>
            <div>
              <ESignaturePad
                field="signature"
                onChange={handleSignatureChange}
                // existingSignatureUrl={null}
                // error={errors.applicant_sign}
                // isDisabled={saveLoading}
              />
            </div> */}
              </div>
              <hr style={{ margin: "32px 0", borderColor: "#919297" }} />
              {/* Location Mismatch Warning */}
              {locationMismatch && locationDistance !== null && (
                <div
                  className="d-flex align-items-start gap-3 p-3 mb-3 rounded-3"
                  style={{
                    background: "#FFF5F5",
                    border: "1px solid #F5C2C7",
                    borderLeft: "4px solid #DC3545",
                  }}
                >
                  {/* Warning Icon */}
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "#FDE2E4",
                    }}
                  >
                    <i
                      className="ti ti-alert-triangle"
                      style={{
                        fontSize: "20px",
                        color: "#DC3545",
                      }}
                    ></i>
                  </div>
                  {/* Message */}
                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-danger">
                      Location Mismatch
                    </h6>
                    <p
                      className="mb-1 text-dark"
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      You are approximately{" "}
                      <strong className="text-danger">
                        {Math.round(locationDistance)} metres
                      </strong>{" "}
                      away from the submitted address.
                    </p>
                    <p
                      className="mb-0 text-secondary"
                      style={{
                        fontSize: "13px",
                        lineHeight: "1.5",
                      }}
                    >
                      Please make sure you are physically present at the address
                      you have provided before proceeding.
                    </p>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end gap-3">
                <button
                  // onClick={() => setShowCancelModal(true)}
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-primary"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn btn-primary"
                  // disabled={locationMismatch}
                >
                  Save & Next
                </button>
              </div>
            </div>
          </>
        )}
        {/* {showCancelModal && (
          <CancelAuthentication
            show={showCancelModal}
            onClose={() => setShowCancelModal(false)}
          />
        )} */}
      </div>
    </>
  );
};
export default LocationVerification;
